import React from "react";
import { ChevronRightIcon, HomeIcon } from "@heroicons/react/20/solid";
import clsx from "clsx";

export interface BreadcrumbsProps {
  title: string | string[];
  source?: string; // The top-level (first) breadcrumb shown next to the current page
  sourceRoute?: string; // If provided, a route that the top-level breadcrumb will link to
  contentInlineWithTitle?: React.ReactNode; // Any content (e.g. buttons) to put at the right of the title
  hideHomeIcon?: boolean; // If true, hide the home icon
}

const Breadcrumbs = ({
  title,
  source = "Privacy Center",
  sourceRoute = "#",
  hideHomeIcon = false,
  contentInlineWithTitle,
}: BreadcrumbsProps) => {
  // Convert the old props format to the new pages format
  const titleArray = Array.isArray(title) ? title : [title];
  const pages = [
    { name: source, href: sourceRoute, current: false },
    ...titleArray.map((item, index) => ({
      name: item,
      href: "#",
      current: index === titleArray.length - 1,
    })),
  ];

  return (
    <div>
      <nav aria-label="Breadcrumb" className="flex pb-3">
        <ol role="list" className="flex items-center space-x-4">
          {!hideHomeIcon && (
            <li>
              <div>
                <a href="/" className="text-gray-400 hover:text-gray-500">
                  <HomeIcon aria-hidden="true" className="h-5 w-5 shrink-0" />
                  <span className="sr-only">Home</span>
                </a>
              </div>
            </li>
          )}
          {pages.map((page, index) => (
            <li key={page.name}>
              <div className="flex items-center">
                {!(hideHomeIcon && index == 0) && (
                  <ChevronRightIcon aria-hidden="true" className="h-5 w-5 shrink-0 text-gray-400" />
                )}
                {index === 0 ? (
                  <a
                    href={page.href}
                    className={clsx(
                      !(hideHomeIcon && index == 0) && "ml-4",
                      "text-sm font-medium text-gray-500 hover:text-gray-700",
                    )}
                  >
                    {page.name}
                  </a>
                ) : (
                  <span
                    className={clsx(
                      !(hideHomeIcon && index == 0) && "ml-4",
                      "text-sm font-medium text-gray-500",
                    )}
                  >
                    {page.name}
                  </span>
                )}
              </div>
            </li>
          ))}
        </ol>
      </nav>
      <div className="pt-3 flex items-center justify-between">
        <span className="text-primary-800 font-bold" style={{ fontSize: "30px" }}>
          {Array.isArray(title) ? title[title.length - 1] : title}
        </span>
        {contentInlineWithTitle && <div>{contentInlineWithTitle}</div>}
      </div>
    </div>
  );
};

export default Breadcrumbs;
